import { useState, useEffect, useCallback } from 'react';
import { reviewsAPI } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import { toast } from 'sonner';
import { Loader2, Send, CheckCircle2, Undo2 } from 'lucide-react';
import { formatDate, formatDateTime } from '../../lib/utils';
import { ActionItemsSection } from './ActionItemsSection';
import { PreviousActionItemsSection } from './PreviousActionItemsSection';
import { ManagementNotesPanel } from './ManagementNotesPanel';
import { TrainingPanel } from './TrainingPanel';

const STATUS_BADGE = {
  DRAFT: 'bg-slate-100 text-slate-700',
  SUBMITTED: 'bg-amber-100 text-amber-800',
  SENT_BACK: 'bg-orange-100 text-orange-800',
  APPROVED: 'bg-emerald-100 text-emerald-800',
};

const RATING_FIELDS = [
  ['product_knowledge', 'Product Knowledge'],
  ['customer_vendor_management', 'Customer / Vendor Management'],
  ['target_achievement', 'Target Achievement'],
  ['crm_tradeiq_usage', 'CRM / TradeIQ Usage'],
  ['rma_management', 'RMA Management', 'Handles RMAs and claims with customers and vendors on time: paperwork, follow-up and recovery.'],
  ['team_management', 'Team Management', 'Guides and follows up their team: targets, CRM/Trade IQ discipline, coaching and escalations.'],
];

export function ReviewDetailModal({ reviewId, open, onClose, onChanged }) {
  const { canAccess } = useAuth();
  const [review, setReview] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({});
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackComment, setSendBackComment] = useState('');

  const canEditModule = canAccess('PERFORMANCE_REVIEWS', 'EDIT');
  const canApproveModule = canAccess('PERFORMANCE_REVIEWS', 'APPROVE');

  const fetchReview = useCallback(() => {
    if (!reviewId) return;
    setLoading(true);
    reviewsAPI.get(reviewId).then((r) => {
      setReview(r.data);
      const criteria = r.data.criteria || [];
      setDraft({
        ...RATING_FIELDS.filter(([k]) => criteria.includes(k)).reduce((acc, [k]) => ({ ...acc, [k]: r.data[k] }), {}),
        comments: r.data.comments || '', target_achievement_pct: r.data.target_achievement_pct ?? '',
        action_items: r.data.action_items || [], previous_action_items: r.data.previous_action_items || [],
        next_review_date: r.data.next_review_date || r.data.suggested_next_review_date || '',
        approver_comments: r.data.approver_comments || '',
      });
    }).catch(() => toast.error('Failed to load review')).finally(() => setLoading(false));
  }, [reviewId]);

  useEffect(() => { if (open) fetchReview(); }, [open, fetchReview]);
  useEffect(() => {
    if (open && (canEditModule || canApproveModule)) {
      reviewsAPI.trainers().then((r) => setTrainers(r.data || [])).catch(() => {});
    }
  }, [open, canEditModule, canApproveModule]);

  if (!open) return null;
  if (loading || !review) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent data-testid="review-detail-modal-loading"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></DialogContent>
      </Dialog>
    );
  }

  const canEditDraft = canEditModule && ['DRAFT', 'SENT_BACK'].includes(review.status);
  const editable = canEditDraft || canApproveModule;
  const canSubmit = canEditModule && ['DRAFT', 'SENT_BACK'].includes(review.status);
  const canApproveNow = canApproveModule && review.status === 'SUBMITTED';
  const criteriaFields = RATING_FIELDS.filter(([key]) => (review.criteria || []).includes(key));
  const isSenior = (review.criteria || []).includes('team_management');

  const saveEdits = async () => {
    setSaving(true);
    try {
      const res = await reviewsAPI.update(review.id, draft);
      setReview(res.data);
      if (!draft.next_review_date && res.data.suggested_next_review_date) {
        setDraft((d) => ({ ...d, next_review_date: res.data.suggested_next_review_date }));
      }
      toast.success('Review updated');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const doSubmit = async () => {
    setSaving(true);
    try {
      await reviewsAPI.update(review.id, draft);
      const res = await reviewsAPI.submit(review.id);
      setReview(res.data);
      toast.success('Review submitted for approval');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally {
      setSaving(false);
    }
  };

  const doApprove = async () => {
    setSaving(true);
    try {
      const res = await reviewsAPI.approve(review.id, {
        next_review_date: draft.next_review_date || undefined, approver_comments: draft.approver_comments,
      });
      setReview(res.data);
      toast.success('Review approved');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setSaving(false);
    }
  };

  const doSendBack = async () => {
    if (!sendBackComment.trim()) { toast.error('A comment is required'); return; }
    setSaving(true);
    try {
      const res = await reviewsAPI.sendBack(review.id, sendBackComment.trim());
      setReview(res.data);
      setSendBackOpen(false);
      toast.success('Review sent back');
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send back');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="review-detail-modal">
      <TooltipProvider>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {review.employee_name}
            <Badge className={STATUS_BADGE[review.status] || 'bg-slate-100 text-slate-700'} data-testid="review-detail-status-badge">{review.status}</Badge>
            {isSenior && <Badge variant="outline" data-testid="review-detail-senior-badge">Sr Executive and above</Badge>}
          </DialogTitle>
        </DialogHeader>

        {review.send_back_comment && (
          <div className="bg-orange-50 border border-orange-200 rounded-md p-2 text-sm text-orange-800" data-testid="review-send-back-comment">
            Sent back: {review.send_back_comment}
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><span className="text-muted-foreground">Review date:</span> {formatDate(review.review_date)}</div>
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground">Overall score:</span> {review.overall_score != null ? review.overall_score.toFixed(2) : '—'}
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0" data-testid="review-detail-criteria-count">
                    {(review.criteria || []).length} criteria
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>Scored on {(review.criteria || []).length} criteria</TooltipContent>
              </Tooltip>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {criteriaFields.map(([key, label, help]) => (
              <div key={key}>
                <Label>{label}</Label>
                {help && <p className="text-xs text-muted-foreground mb-1">{help}</p>}
                {editable ? (
                  <Select value={draft[key] ? String(draft[key]) : ''} onValueChange={(v) => setDraft({ ...draft, [key]: parseInt(v) })}>
                    <SelectTrigger data-testid={`review-rating-${key}`}><SelectValue placeholder="Rate 1-5" /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <p className="text-sm py-1">{review[key] ?? '—'}</p>
                )}
              </div>
            ))}
          </div>

          <div>
            <Label>Target Achievement %</Label>
            {editable ? (
              <Input type="number" step="0.1" value={draft.target_achievement_pct}
                onChange={(e) => setDraft({ ...draft, target_achievement_pct: e.target.value })} data-testid="review-target-pct-input" />
            ) : <p className="text-sm py-1">{review.target_achievement_pct != null ? review.target_achievement_pct : '—'}</p>}
          </div>

          <div>
            <Label>Comments</Label>
            {editable ? (
              <Textarea value={draft.comments} onChange={(e) => setDraft({ ...draft, comments: e.target.value })} data-testid="review-comments-input" />
            ) : <p className="text-sm py-1 whitespace-pre-wrap">{review.comments || '—'}</p>}
          </div>

          <div>
            <Label>Action Items</Label>
            <ActionItemsSection items={draft.action_items} trainers={trainers} editable={editable}
              onChange={(items) => setDraft({ ...draft, action_items: items })} />
          </div>

          <div>
            <Label>Carried-forward action items</Label>
            {(draft.previous_action_items || []).length === 0 ? (
              <p className="text-sm text-muted-foreground py-1">—</p>
            ) : (
              <PreviousActionItemsSection items={draft.previous_action_items} editable={canEditDraft}
                onChange={(items) => setDraft({ ...draft, previous_action_items: items })} />
            )}
          </div>

          {canApproveModule && (
            <div>
              <Label>Approver Comments</Label>
              <Textarea value={draft.approver_comments} onChange={(e) => setDraft({ ...draft, approver_comments: e.target.value })}
                data-testid="review-approver-comments-input" />
            </div>
          )}

          {canEditDraft && (
            <div>
              <Label>Next Review Date (proposed)</Label>
              <Input type="date" value={draft.next_review_date} onChange={(e) => setDraft({ ...draft, next_review_date: e.target.value })}
                data-testid="review-proposed-next-date-input" />
              <p className="text-xs text-muted-foreground mt-1">
                Suggested from the ratings — change it if a different date fits better. The approver can still override this.
              </p>
            </div>
          )}

          {review.status === 'SUBMITTED' && canApproveModule && (
            <div>
              <Label>Next Review Date (proposed by {review.created_by_name || 'the creator'} — you can override)</Label>
              <Input type="date" value={draft.next_review_date} onChange={(e) => setDraft({ ...draft, next_review_date: e.target.value })}
                data-testid="review-next-date-input" />
            </div>
          )}
          {review.next_review_date && review.status === 'APPROVED' && (
            <div className="text-sm"><span className="text-muted-foreground">Next review date:</span> {formatDate(review.next_review_date)}</div>
          )}

          {review.history && (
            <div className="border-t pt-3">
              <Label>History</Label>
              <div className="space-y-1 mt-1 max-h-32 overflow-y-auto" data-testid="review-history-log">
                {review.history.map((h, i) => (
                  <p key={i} className="text-xs text-muted-foreground">{h.by_name} {h.action.toLowerCase()} — {formatDateTime(h.at)}</p>
                ))}
              </div>
            </div>
          )}

          <ManagementNotesPanel reviewId={review.id} />
          <TrainingPanel reviewId={review.id} />
        </div>

        <DialogFooter className="gap-2">
          {editable && (
            <Button variant="outline" onClick={saveEdits} disabled={saving} data-testid="review-save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          )}
          {canSubmit && (
            <Button onClick={doSubmit} disabled={saving} data-testid="review-submit-btn">
              <Send className="h-4 w-4 mr-1" /> Submit
            </Button>
          )}
          {canApproveNow && (
            <>
              <Button variant="outline" onClick={() => setSendBackOpen(true)} disabled={saving} data-testid="review-send-back-btn">
                <Undo2 className="h-4 w-4 mr-1" /> Send Back
              </Button>
              <Button onClick={doApprove} disabled={saving} data-testid="review-approve-btn">
                <CheckCircle2 className="h-4 w-4 mr-1" /> Approve
              </Button>
            </>
          )}
        </DialogFooter>

        {sendBackOpen && (
          <div className="border-t pt-3 space-y-2" data-testid="send-back-comment-panel">
            <Label>Reason for sending back (required)</Label>
            <Textarea value={sendBackComment} onChange={(e) => setSendBackComment(e.target.value)} data-testid="send-back-comment-input" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setSendBackOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={doSendBack} data-testid="send-back-confirm-btn">Confirm Send Back</Button>
            </div>
          </div>
        )}
      </TooltipProvider>
      </DialogContent>
    </Dialog>
  );
}
